<script setup lang="ts">
import { onBeforeUnmount, ref, watch } from "vue";
import { ApiClientError } from "../api/client";
import { createParameterSetTemplate, getParameterSetTemplate, getParameterTemplateSource, listParameterSetTemplates,
  type ParameterSetTemplate, type ParameterTemplateSummary, type TemplateCreateRequest } from "../api/parameter-set-templates";
import { canonical, parameterHash, pendingRequest } from "./custom-parameters/template-snapshot";

const props = defineProps<{ productId: string; disabled: boolean; dirty: boolean; canApply: boolean; hasParameters: boolean }>();
const emit = defineEmits<{ apply: [template: ParameterSetTemplate] }>();
const panel = ref<"save" | "apply" | null>(null), name = ref(""), working = ref(false), message = ref("");
const source = ref<Awaited<ReturnType<typeof getParameterTemplateSource>> | null>(null);
const items = ref<ParameterTemplateSummary[]>([]), selected = ref<ParameterSetTemplate | null>(null), selectedId = ref("");
const page = ref(1), hasMore = ref(false), pending = ref<TemplateCreateRequest | null>(null), recoveryError = ref(false);
let generation = 0, disposed = false;
onBeforeUnmount(() => { disposed = true; generation++; });
watch(() => props.productId, () => {
  generation++; pending.value = null; items.value = []; selectedId.value = ""; panel.value = null; selected.value = null; message.value = ""; working.value = false; source.value = null; recoveryError.value = false;
  try { pending.value = pendingRequest<TemplateCreateRequest>("create", props.productId).read(); }
  catch (cause) { recoveryError.value = true; message.value = cause instanceof Error ? cause.message : "待核对记录无法读取"; }
  if (pending.value) message.value = "上次模板保存结果尚未核对，请先点击核对保存结果。";
}, { immediate: true });
function current(version: number) { return !disposed && version === generation; }
async function run(task: (version: number) => Promise<void>) {
  if (working.value || props.disabled || recoveryError.value) return;
  const version = ++generation; working.value = true; message.value = "";
  try { await task(version); }
  catch (cause) { if (current(version)) message.value = cause instanceof Error ? cause.message : "操作未完成"; }
  finally { if (current(version)) working.value = false; }
}
async function openSave() {
  if (props.dirty) { message.value = "请先保存当前商品参数和图片，再保存为模板。"; return; }
  await run(async version => {
    const result = await getParameterTemplateSource(props.productId);
    if (!current(version)) return;
    if (!result.snapshot.groups.length) throw new Error("当前商品没有有效参数，不能保存为空模板");
    source.value = result; panel.value = "save";
  });
}
async function openApply(nextPage = 1) {
  if (!props.canApply) return;
  await run(async version => {
    const result = await listParameterSetTemplates(nextPage);
    if (!current(version)) return;
    items.value = result.items; page.value = result.page; hasMore.value = result.hasMore;
    selectedId.value = ""; selected.value = null; panel.value = "apply";
  });
}
async function selectTemplate() {
  const id = selectedId.value; selected.value = null;
  if (!id) return;
  await run(async version => {
    const result = await getParameterSetTemplate(id);
    if (!current(version)) return;
    if (await parameterHash(result.snapshot) !== result.snapshotHash) throw new Error("模板内容校验不一致，请重新读取");
    if (current(version)) selected.value = result;
  });
}
async function acceptSaved(actual: ParameterSetTemplate, expected: TemplateCreateRequest, version: number) {
  if (actual.id !== expected.id || actual.name !== expected.name || actual.requestHash !== await parameterHash(expected)
    || actual.snapshotHash !== await parameterHash(actual.snapshot)) throw new Error("保存结果与本次请求不一致，请保留操作记录联系管理员");
  if (!current(version) || props.productId !== expected.sourceProductId) return;
  pendingRequest<TemplateCreateRequest>("create", expected.sourceProductId).clear(); pending.value = null; panel.value = null; name.value = "";
  message.value = `模板“${actual.name}”已保存并读回核对，新商品可以应用。`;
}
async function submit() {
  if (pending.value || props.dirty || !source.value) return;
  const templateName = name.value.normalize("NFKC").trim();
  if (!templateName || templateName.length > 100) { message.value = "请输入1至100字的模板名称"; return; }
  await run(async version => {
    const input: TemplateCreateRequest = { id: crypto.randomUUID(), name: templateName, sourceProductId: props.productId, sourceRevision: source.value!.revision };
    pendingRequest<TemplateCreateRequest>("create", props.productId).write(input); pending.value = input;
    try {
      const actual = await createParameterSetTemplate(input);
      if (!current(version)) return;
      await acceptSaved(actual, input, version);
    } catch (cause) {
      if (cause instanceof ApiClientError && [400, 403, 404, 409, 422].includes(cause.status)) {
        pendingRequest<TemplateCreateRequest>("create", input.sourceProductId).clear(); if (current(version)) pending.value = null;
      }
      throw cause;
    }
  });
}
async function verify() {
  if (!pending.value) return;
  await run(async version => {
    const expected = JSON.parse(JSON.stringify(pending.value)) as TemplateCreateRequest;
    const actual = await getParameterSetTemplate(expected.id);
    if (current(version)) await acceptSaved(actual, expected, version);
  });
}
function apply() {
  if (!selected.value || !props.canApply || working.value || props.disabled || pending.value) return;
  emit("apply", JSON.parse(canonical(selected.value)) as ParameterSetTemplate); panel.value = null;
}
function canLeave() { return !working.value && !pending.value && !recoveryError.value; }
defineExpose({ canLeave });
</script>

<template>
  <section
    class="set-template-tools"
    aria-label="整套参数模板"
  >
    <div class="template-buttons">
      <button
        type="button"
        :disabled="disabled || working || !!pending || recoveryError || !hasParameters"
        @click="openSave"
      >
        保存为模板
      </button>
      <button
        type="button"
        :disabled="disabled || working || !!pending || recoveryError || !canApply"
        @click="openApply()"
      >
        应用模板
      </button>
      <button
        v-if="pending"
        type="button"
        :disabled="disabled || working"
        @click="verify"
      >
        核对保存结果
      </button>
    </div>
    <p>模板保存整套参数和选项。应用后各商品独立维护；已有参数的商品不能覆盖。</p>
    <p
      v-if="message"
      role="status"
    >
      {{ message }}{{ pending ? ' 请只核对结果，不要重复保存。' : '' }}
    </p>
    <div
      v-if="panel === 'save'"
      class="template-panel"
    >
      <label>模板名称 <input
        v-model="name"
        maxlength="100"
        :disabled="working || !!pending"
        placeholder="例如：画册通用参数"
      ></label>
      <p>将保存 {{ source?.snapshot.groups.length }} 组已保存参数：</p>
      <ul>
        <li
          v-for="group in source?.snapshot.groups"
          :key="group.code"
        >
          {{ group.name }} · {{ group.inputType === 'NUMBER' ? '数字输入' : group.inputType === 'TEXT' ? '文字或尺寸输入' : group.values.length + '个选项' }}
        </li>
      </ul>
      <button
        type="button"
        :disabled="disabled || working || dirty || !!pending"
        @click="submit"
      >
        确认保存模板
      </button>
      <button
        type="button"
        :disabled="working || !!pending"
        @click="panel = null"
      >
        取消
      </button>
    </div>
    <div
      v-if="panel === 'apply'"
      class="template-panel"
    >
      <label>选择模板 <select
        v-model="selectedId"
        :disabled="working"
        @change="selectTemplate"
      ><option value="">请选择</option><option
        v-for="item in items"
        :key="item.id"
        :value="item.id"
      >{{ item.name }} · {{ item.groupCount }}组 / {{ item.valueCount }}项</option></select></label>
      <p v-if="!items.length">
        还没有整套参数模板，请先在已有商品中保存。
      </p>
      <div class="template-buttons">
        <button
          type="button"
          :disabled="working || page <= 1"
          @click="openApply(page - 1)"
        >
          上一页
        </button><span>第 {{ page }} 页</span><button
          type="button"
          :disabled="working || !hasMore"
          @click="openApply(page + 1)"
        >
          下一页
        </button>
      </div>
      <div
        v-if="selected"
        class="template-preview"
      >
        <h3>{{ selected.name }}</h3>
        <div
          v-for="group in selected.snapshot.groups"
          :key="group.code"
        >
          <strong>{{ group.name }}{{ group.required ? '（必填）' : '' }}{{ group.unit ? ' · ' + group.unit : '' }}</strong>
          <p v-if="group.inputType === 'NUMBER'">
            最小值 {{ group.config?.min ?? '未设置' }} · 最大值 {{ group.config?.max ?? '未设置' }} · 步长 {{ group.config?.step ?? '默认' }}
          </p>
          <p v-if="group.code === 'FINISHED_SIZE'">
            尺寸输入设置将随模板带入。
          </p>
          <span
            v-for="value in group.values"
            :key="value.code"
            class="preview-option"
          ><img
            v-if="value.imageUrl"
            :src="value.imageUrl"
            alt=""
          >{{ value.name }}</span>
        </div>
      </div>
      <button
        type="button"
        :disabled="disabled || working || !selected || !canApply"
        @click="apply"
      >
        带入参数草稿
      </button>
      <button
        type="button"
        :disabled="working"
        @click="panel = null"
      >
        取消
      </button>
      <p>带入后可调整装订方式等选项，点击“保存定制参数”后才保存到新商品。</p>
    </div>
  </section>
</template>

<style scoped>
.set-template-tools { padding:12px 0; border-bottom:1px solid #eee; margin-bottom:12px; }
.template-buttons { display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
button,input,select { font:inherit; border:1px solid #ddd; border-radius:4px; padding:7px 10px; background:white; }
button { cursor:pointer; } button:disabled { cursor:not-allowed; color:#888; background:#f5f5f5; }
p { font-size:13px; color:#666; } .template-panel { border:1px solid #ddd; padding:14px; margin-top:12px; }
.template-panel>button { margin-right:8px; } .template-preview { max-height:360px; overflow:auto; margin:12px 0; }
.template-preview>div { margin-bottom:12px; } .preview-option { display:inline-flex; gap:5px; align-items:center; padding:5px; margin:4px; background:#f5f5f5; }
.preview-option img { width:50px; height:50px; object-fit:contain; }
</style>
